import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { attachmentUrl } from "../lib/api";
import {
  CARD_PHOTO_BADGE_LABEL,
  PHOTO_DELETE_LABEL,
  PHOTO_LIGHTBOX_CLOSE_LABEL,
  PHOTO_REMOVE_CONFIRM_BODY,
  PHOTO_REMOVE_CONFIRM_LABEL,
  PHOTO_REMOVE_CONFIRM_TITLE,
  REMOVE_CARD_PHOTO_LABEL,
  SET_CARD_PHOTO_LABEL,
  photoAltText,
} from "../lib/recordsCopy";
import type { AttachmentOut } from "../types";
import { ConfirmDialog } from "./ConfirmDialog";

export interface PhotoLightboxProps {
  /** `null` renders nothing — matches this app's existing "the overlay IS
   * its own open/closed state" convention (`AnimalDetailOverlay`'s own
   * `openAnimal` pattern). */
  photo: AttachmentOut | null;
  animalName: string;
  isAdmin: boolean;
  /** Sprint-013 B2 — true when this photo is the animal's RESOLVED card
   * photo (`animal.primary_photo_attachment_id`) — drives both the
   * read-visible-to-everyone indicator text and which of the two admin-
   * only toggle actions (Set / Remove as card photo) renders. */
  isPrimary: boolean;
  onClose: () => void;
  onRemove: (attachmentId: string) => Promise<void>;
  /** Sprint-013 B2 (SA ruling Decision 4.4) — `PUT /animals/{id}/primary-
   * photo`'s `attachment_id` shape directly: this photo's id to SET it as
   * the card photo, or `null` to CLEAR the explicit selection (reverting
   * to the newest-photo auto-select). The caller (`AnimalDetailOverlay`'s
   * `PhotosPanel`) owns the try/catch + `onNotify` copy, matching every
   * other admin mutation in this file's sibling panels. */
  onSetPrimaryPhoto: (attachmentId: string | null) => Promise<void>;
}

/**
 * Sprint-010 C3 (design-phase-c.md §2.6) — the Photos tab's full-view
 * popover: "a lightweight centered lightbox/popover, not a second
 * full-screen `.overlay-view`" — mirrors the calendar's own "+2 more"/
 * done-chip popover precedent (design-phase-b.md §2.3/§2.10).
 *
 * The full-size `<img>` here is genuinely standalone content (not inside
 * a button, unlike the grid's own thumbnails), so it gets the real
 * `alt="Photo of <animal>"` text (§7's image-alt strategy) rather than
 * the grid's `alt=""` + labeled-button pattern.
 *
 * Sprint-013 B2 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
 * Decision 4.4) — the "Set as card photo"/"Remove as card photo" toggle
 * (admin/api only, reusing `.secondary-btn` per the design spec's own
 * "reuse existing button patterns" instruction) plus a plain-text
 * current-card-photo indicator visible to every role, matching how the
 * "Edited" chip elsewhere in this app is read-visible regardless of the
 * write affordance next to it. Deliberately NOT wrapped in
 * `ConfirmDialog` — this is a low-risk, fully reversible pointer flip
 * (the photo itself is untouched either way), unlike the destructive
 * "Delete photo" (hard-delete) action, which lives on its own row below a
 * divider and always requires the `ConfirmDialog`.
 */
export function PhotoLightbox({
  photo,
  animalName,
  isAdmin,
  isPrimary,
  onClose,
  onRemove,
  onSetPrimaryPhoto,
}: PhotoLightboxProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [settingPrimary, setSettingPrimary] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sprint-011 C1 (TD-012) — backdrop + Close route through the hook's
  // returned `close`, not `onClose` directly.
  const { close } = useModalA11y(containerRef, { active: photo !== null, onClose });

  if (!photo) return null;

  async function handleConfirmRemove() {
    setRemoving(true);
    try {
      await onRemove(photo!.id);
    } finally {
      setRemoving(false);
      setConfirmOpen(false);
    }
  }

  async function handleTogglePrimary() {
    setSettingPrimary(true);
    try {
      await onSetPrimaryPhoto(isPrimary ? null : photo!.id);
    } finally {
      setSettingPrimary(false);
    }
  }

  return (
    <>
      <div class="photo-lightbox-backdrop" onClick={close} />
      <div
        ref={containerRef}
        class="photo-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label={photoAltText(animalName)}
      >
        <img src={attachmentUrl(photo.id)} alt={photoAltText(animalName)} />
        {isPrimary ? <p class="photo-lightbox-primary-hint">{CARD_PHOTO_BADGE_LABEL}</p> : null}
        <div class="photo-lightbox-actions">
          <button type="button" class="secondary-btn" onClick={close}>
            {PHOTO_LIGHTBOX_CLOSE_LABEL}
          </button>
          {isAdmin ? (
            <button
              type="button"
              class="secondary-btn"
              disabled={settingPrimary}
              onClick={() => void handleTogglePrimary()}
            >
              {isPrimary ? REMOVE_CARD_PHOTO_LABEL : SET_CARD_PHOTO_LABEL}
            </button>
          ) : null}
        </div>
        {/* The hard delete sits on its own row below a divider, never
         * beside the reversible card-photo toggle, and always goes
         * through the confirm below. */}
        {isAdmin ? (
          <div class="photo-lightbox-danger">
            <button
              type="button"
              class="secondary-btn is-destructive"
              disabled={removing}
              onClick={() => setConfirmOpen(true)}
            >
              {PHOTO_DELETE_LABEL}
            </button>
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={PHOTO_REMOVE_CONFIRM_TITLE}
        body={PHOTO_REMOVE_CONFIRM_BODY}
        confirmLabel={PHOTO_REMOVE_CONFIRM_LABEL}
        destructive
        onConfirm={() => void handleConfirmRemove()}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
